import { Controller } from "@hotwired/stimulus";
import Fuse from "fuse.js";
import * as bootstrap from "bootstrap";

export default class extends Controller {
  static targets = [
    "searchInput",
    "clearButton",
    "resultsCount",
    "templateRow",
  ];
  static values = {
    importUrl: String,
    importWithMusclesUrl: String,
    csrfToken: String,
  };

  connect() {
    this.initializeSearch();
  }

  initializeSearch() {
    if (!this.hasSearchInputTarget || this.templateRowTargets.length === 0) {
      return;
    }

    // Build search data from DOM elements
    this.searchData = this.templateRowTargets.map((row, index) => ({
      id: index,
      element: row,
      name: this.getTextFromCell(row, 0),
      primaryMuscle: this.getTextFromCell(row, 1),
      secondaryMuscles: this.getTextFromCell(row, 2),
      equipment: this.getTextFromCell(row, 3),
      mechanic: this.getTextFromCell(row, 4),
      level: this.getTextFromCell(row, 5),
    }));

    // Initialize Fuse.js
    this.fuse = new Fuse(this.searchData, {
      keys: [
        { name: "name", weight: 0.7 },
        { name: "primaryMuscle", weight: 0.5 },
        { name: "secondaryMuscles", weight: 0.3 },
        { name: "equipment", weight: 0.4 },
        { name: "mechanic", weight: 0.3 },
        { name: "level", weight: 0.2 },
      ],
      threshold: 0.3, // Lower = more strict matching
      includeScore: true,
      minMatchCharLength: 2,
    });

    this.updateClearButton();
  }

  search() {
    const searchTerm = this.searchInputTarget.value.trim();

    if (!searchTerm) {
      // Show all rows when search is empty
      this.templateRowTargets.forEach((row) => {
        row.style.display = "";
      });
      this.updateResultsCount(this.templateRowTargets.length);
      this.updateClearButton();
      return;
    }

    if (!this.fuse) {
      return;
    }

    // Search using Fuse.js
    const results = this.fuse.search(searchTerm);

    // Hide all rows first
    this.templateRowTargets.forEach((row) => {
      row.style.display = "none";
    });

    // Show matching rows
    results.forEach((result) => {
      const element = result.item.element;
      element.style.display = "";
    });

    this.updateResultsCount(results.length);
    this.updateClearButton();
  }

  clearSearch() {
    this.searchInputTarget.value = "";
    this.search();
    this.searchInputTarget.focus();
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.clearSearch();
    }
  }

  async importTemplate(event) {
    const button = event.currentTarget;
    const templateData = button.dataset.template;

    // Disable button and show loading state
    button.disabled = true;
    button.innerHTML =
      '<span class="spinner-border spinner-border-sm" role="status"></span> Importing...';

    try {
      const response = await fetch(this.importUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfTokenValue,
        },
        body: "template_data=" + encodeURIComponent(templateData),
      });

      const data = await response.json();

      if (data.success) {
        this.showMessage(data.message, "success");
        // Mark as imported
        button.innerHTML = '<i class="bi bi-check-circle"></i> Imported';
        button.classList.remove("btn-success");
        button.classList.add("btn-secondary");
        button.disabled = true;
      } else if (data.requires_muscle_selection) {
        // Show muscle selection modal
        this.showMuscleSelectionModal(data, button);
      } else {
        this.showMessage(data.message, "danger");
        // Reset button
        button.disabled = false;
        button.innerHTML = '<i class="bi bi-plus-circle"></i> Import';
      }
    } catch (error) {
      console.error("Error:", error);
      this.showMessage(
        "An error occurred while importing the template.",
        "danger"
      );
      // Reset button
      button.disabled = false;
      button.innerHTML = '<i class="bi bi-plus-circle"></i> Import';
    }
  }

  getTextFromCell(row, cellIndex) {
    const cells = row.querySelectorAll("td");
    if (cellIndex >= cells.length) return "";

    const cell = cells[cellIndex];
    return (cell.textContent || cell.innerText || "").trim();
  }

  updateResultsCount(count) {
    if (this.hasResultsCountTarget) {
      this.resultsCountTarget.textContent = count;
    }
  }

  updateClearButton() {
    if (this.hasClearButtonTarget) {
      const hasSearchTerm = this.searchInputTarget.value.trim().length > 0;
      this.clearButtonTarget.style.display = hasSearchTerm ? "block" : "none";
    }
  }

  showMessage(message, type) {
    const messageContainer = document.getElementById("message-container");
    if (!messageContainer) return;

    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible fade show`;
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;

    messageContainer.appendChild(alertDiv);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove();
      }
    }, 5000);
  }

  showMuscleSelectionModal(data, originalButton) {
    // Reset button state
    originalButton.disabled = false;
    originalButton.innerHTML = '<i class="bi bi-plus-circle"></i> Import';

    // Store reference to original button and template data
    this.currentImportButton = originalButton;
    this.currentTemplateData = data.template_data;

    // Populate modal with exercise info
    document.getElementById("exercise-name").textContent =
      data.template_data.name;

    // Show original muscles from JSON
    document.getElementById("json-primary-muscle").textContent =
      data.template_data.primaryMuscles?.[0] || "None";
    document.getElementById("json-secondary-muscles").textContent =
      data.template_data.secondaryMuscles?.length > 0
        ? data.template_data.secondaryMuscles.join(", ")
        : "None";

    // Populate muscle dropdowns
    this.populateMuscleSelects(data.available_muscles, data.template_data);

    // Show the modal
    const modal = new bootstrap.Modal(
      document.getElementById("muscleSelectionModal")
    );
    modal.show();

    // Set up modal event listeners
    this.setupModalEventListeners();
  }

  populateMuscleSelects(availableMuscles, templateData) {
    const primarySelect = document.getElementById("primary-muscle-select");
    const originalPrimary = document.getElementById("original-primary-muscle");

    // Clear and populate primary muscle select
    primarySelect.innerHTML =
      '<option value="">Select primary muscle...</option>';
    availableMuscles.forEach((muscle) => {
      const option = document.createElement("option");
      option.value = muscle;
      option.textContent = muscle;
      primarySelect.appendChild(option);
    });

    // Show original primary muscle
    originalPrimary.textContent = templateData.primaryMuscles?.[0] || "None";

    // Clear secondary muscles container
    const container = document.getElementById("secondary-muscles-container");
    container.innerHTML = "";

    // Add secondary muscle selects for original muscles
    if (templateData.secondaryMuscles?.length > 0) {
      templateData.secondaryMuscles.forEach((muscle) => {
        this.addSecondaryMuscleSelect(availableMuscles, muscle);
      });
    }
  }

  addSecondaryMuscleSelect(availableMuscles, selectedMuscle = "") {
    const container = document.getElementById("secondary-muscles-container");
    const selectDiv = document.createElement("div");
    selectDiv.className = "input-group mb-2";

    selectDiv.innerHTML = `
      <select class="form-select secondary-muscle-select">
        <option value="">Select secondary muscle...</option>
        ${availableMuscles
          .map(
            (muscle) =>
              `<option value="${muscle}" ${
                muscle === selectedMuscle ? "selected" : ""
              }>${muscle}</option>`
          )
          .join("")}
      </select>
      <button class="btn btn-outline-danger remove-secondary-muscle" type="button">
        <i class="bi bi-trash"></i>
      </button>
    `;

    container.appendChild(selectDiv);

    // Add remove functionality
    selectDiv
      .querySelector(".remove-secondary-muscle")
      .addEventListener("click", () => {
        selectDiv.remove();
      });
  }

  setupModalEventListeners() {
    // Add secondary muscle button
    document.getElementById("add-secondary-muscle").onclick = () => {
      const availableMuscles = Array.from(
        document.getElementById("primary-muscle-select").options
      )
        .slice(1) // Skip the first "Select..." option
        .map((option) => option.value);
      this.addSecondaryMuscleSelect(availableMuscles);
    };

    // Confirm selection button
    document.getElementById("confirm-muscle-selection").onclick = () => {
      this.confirmMuscleSelection();
    };
  }

  async confirmMuscleSelection() {
    const primaryMuscle = document.getElementById(
      "primary-muscle-select"
    ).value;
    const secondaryMuscles = Array.from(
      document.querySelectorAll(".secondary-muscle-select")
    )
      .map((select) => select.value)
      .filter((value) => value !== "");

    if (!primaryMuscle) {
      this.showMessage("Please select a primary muscle.", "danger");
      return;
    }

    const button = this.currentImportButton;
    button.disabled = true;
    button.innerHTML =
      '<span class="spinner-border spinner-border-sm" role="status"></span> Importing...';

    try {
      // Build form data properly for arrays
      const formData = new URLSearchParams();
      formData.append(
        "template_data",
        JSON.stringify(this.currentTemplateData)
      );
      formData.append("primary_muscle", primaryMuscle);

      // Add secondary muscles as individual parameters
      secondaryMuscles.forEach((muscle) => {
        formData.append("secondary_muscles[]", muscle);
      });

      const response = await fetch(this.importWithMusclesUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfTokenValue,
        },
        body: formData,
      });

      const data = await response.json();

      // Hide modal
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("muscleSelectionModal")
      );
      modal.hide();

      if (data.success) {
        this.showMessage(data.message, "success");
        // Mark as imported
        button.innerHTML = '<i class="bi bi-check-circle"></i> Imported';
        button.classList.remove("btn-success");
        button.classList.add("btn-secondary");
        button.disabled = true;
      } else {
        this.showMessage(data.message, "danger");
        // Reset button
        button.disabled = false;
        button.innerHTML = '<i class="bi bi-plus-circle"></i> Import';
      }
    } catch (error) {
      console.error("Error:", error);
      this.showMessage(
        "An error occurred while importing the template.",
        "danger"
      );
      // Reset button
      button.disabled = false;
      button.innerHTML = '<i class="bi bi-plus-circle"></i> Import';
    }
  }
}
